import * as Yup from 'yup';

import {
  CreateSubmissionField,
  getSubmissionFieldRequiredErrorMessage,
  getSubmissionFieldWrongTypeErrorMessage,
} from '@converge/data';

import moment from 'moment';
import { validateYupDomain } from '@converge/utils';

const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const stringRequired = (fieldName: CreateSubmissionField) => {
  return Yup.string()
    .typeError(getSubmissionFieldWrongTypeErrorMessage(fieldName))
    .required(getSubmissionFieldRequiredErrorMessage())
    .trim()
    .min(1, getSubmissionFieldRequiredErrorMessage());
};

export const customDateStartOfDay = () => {
  return Yup.date()
    .required('This field is required') // Add required validation
    .transform((value, originalValue) => {
      if (!originalValue) return null;

      // Handle mm/dd/yyyy format
      const parsedMoment = moment(
        originalValue,
        ['MM/DD/YYYY', moment.ISO_8601],
        true
      );

      if (!parsedMoment.isValid()) return new Date('invalid'); // This will trigger typeError

      const adjustedMoment = parsedMoment.set({
        hour: 0,
        minute: 1,
        second: 0,
        millisecond: 0,
      });

      return adjustedMoment.toDate();
    })
    .typeError('Enter a valid date');
};

export const createSubmissionFieldsSchema = Yup.object({
  [CreateSubmissionField.LegalName]: stringRequired(
    CreateSubmissionField.LegalName
  ),
  [CreateSubmissionField.TradingName]: Yup.string().typeError(
    getSubmissionFieldWrongTypeErrorMessage(CreateSubmissionField.TradingName)
  ),
  [CreateSubmissionField.Domain]: validateYupDomain(
    stringRequired(CreateSubmissionField.Domain)
  ),
  [CreateSubmissionField.Street1]: stringRequired(
    CreateSubmissionField.Street1
  ),
  [CreateSubmissionField.Street2]: Yup.string(),
  [CreateSubmissionField.City]: stringRequired(CreateSubmissionField.City),
  [CreateSubmissionField.RegionUuid]: stringRequired(
    CreateSubmissionField.RegionUuid
  ),
  [CreateSubmissionField.PostalCode]: stringRequired(
    CreateSubmissionField.PostalCode
  ),
  [CreateSubmissionField.CountryUuid]: stringRequired(
    CreateSubmissionField.CountryUuid
  ).uuid(),
  [CreateSubmissionField.ContactFirstName]: Yup.string().optional(),
  [CreateSubmissionField.ContactLastName]: Yup.string().optional(),
  [CreateSubmissionField.ContactEmailAddress]: Yup.string().optional(),
  [CreateSubmissionField.ContactPhoneNumber]: Yup.string().optional(),
  [CreateSubmissionField.CurrentDomesticRevenue]: stringRequired(
    CreateSubmissionField.CurrentDomesticRevenue
  ),
  [CreateSubmissionField.CurrentForeignRevenue]: Yup.string().optional(),
  [CreateSubmissionField.ProjectDomesticRevenue]: Yup.string().optional(),
  [CreateSubmissionField.ProjectForeignRevenue]: Yup.string().optional(),
  [CreateSubmissionField.RatedRevenueOverride]: stringRequired(
    CreateSubmissionField.RatedRevenueOverride
  ),
  [CreateSubmissionField.NumEmployees]: Yup.string().optional(),
  [CreateSubmissionField.EstablishedYear]: Yup.string().optional(),
  [CreateSubmissionField.IndustrySector]: Yup.number()
    .optional()
    .typeError(
      getSubmissionFieldWrongTypeErrorMessage(
        CreateSubmissionField.IndustrySector
      )
    )
    .positive(),
  //Requested Policy
  [CreateSubmissionField.ProductType]: stringRequired(
    CreateSubmissionField.ProductType
  ),

  [CreateSubmissionField.PartnerUuid]: Yup.string().optional(),

  [CreateSubmissionField.Type]: stringRequired(CreateSubmissionField.Type),
  [CreateSubmissionField.AggregateLimit]: Yup.string().optional(),
  [CreateSubmissionField.PolicyRetention]: Yup.string().optional(),

  [CreateSubmissionField.PolicyStartDate]: customDateStartOfDay().optional(),
  [CreateSubmissionField.PolicyEndDate]: customDateStartOfDay().optional(),
  //Submission
  [CreateSubmissionField.ReceivedAt]: customDateStartOfDay().optional(),
  [CreateSubmissionField.ReceivedBy]: stringRequired(
    CreateSubmissionField.ReceivedBy
  ).matches(emailRegex, 'Please enter a valid email address'),
  [CreateSubmissionField.DueDate]: customDateStartOfDay().optional(),
  [CreateSubmissionField.Notes]: Yup.string().optional(),
  //Broker
  [CreateSubmissionField.BrokerageUuid]: stringRequired(
    CreateSubmissionField.BrokerageUuid
  ),
  [CreateSubmissionField.BrokerAgentUuid]: stringRequired(
    CreateSubmissionField.BrokerAgentUuid
  ),
  [CreateSubmissionField.UserUuid]: stringRequired(
    CreateSubmissionField.UserUuid
  ),
});
